import React, { useState, useCallback, useEffect } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { Clear } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useTranslation } from 'react-i18next';
import { getIncomingBankPayments } from 'src/shared/services/incomingBankPaymanetService';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useGlobals } from 'src/shared/contexts/appContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { IncomingBankPaymentStatusEnum } from 'src/shared/models/enums/incomingBankPaymentStatus.enum';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { IncomingBankPayment } from '../models/incomingBankPayment';
import { IncomingBankPaymentStatuses } from './incomingBankPaymentStatuses';
import { IncomingBankPaymentSearchFilter } from '../models/incomingBankPaymentSearchFilter';
import { IncomingBankPaymentEditModal } from './incomingBankPaymentEditModal';

type IncomingBankPaymentDataGridProps = {
  searchFilter: IncomingBankPaymentSearchFilter;
};

const IncomingBankPaymentDataGrid = (props: IncomingBankPaymentDataGridProps) => {
  const { searchFilter } = props;
  const { t } = useTranslation(['member', 'common']);
  const { dateStamp } = useGlobals();

  const isStatusMatched = searchFilter.status === IncomingBankPaymentStatusEnum.Matched;

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedIncomingBankPayment, setSelectedIncomingBankPayment] = useState<IncomingBankPayment>();

  const isMounted = useIsMounted();
  const [rows, setRows] = useState<IncomingBankPayment[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: false, // set to true later
    totalRows: 0, // set to other prop later
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'date', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const handleRowClick = (params) => {
    const item = rows.filter((x) => x.transaction_id === params.row.transaction_id)[0];
    setSelectedIncomingBankPayment(item);
    setShowDetailsModal(true);
    ConsoleHelper.log('clicked', item);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getIncomingBankPayments(
        data.page,
        data.pageSize,
        sortModel[0].field,
        sortModel[0].sort,
        searchFilter.searchString,
        searchFilter.ownerId,
        searchFilter.status,
      );
      if (isMounted()) {
        ConsoleHelper.log(res.data.results, dateStamp);
        updateData('totalRows', res.data.totalItems);
        setRows(extendObjectWithId(res.data.results, 'transaction_id'));
      }
      ConsoleHelper.log(res);
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, sortModel, searchFilter.searchString, searchFilter.ownerId, searchFilter.status, isMounted, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('invoice:date'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDate(params.row.date)}</>;
      },
    },
    {
      field: 'amount',
      headerName: t('invoice:amount'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { amount } = params.row;
        return (amount && <AmountWithCurrency amount={amount} />) || '-';
      },
    },
    {
      field: 'reference',
      headerName: t('invoice:reference'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.reference}</>;
      },
    },
    {
      field: 'primaryAccount',
      headerName: t('invoice:bgNummer'),
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.primaryAccount}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:status'),
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <IncomingBankPaymentStatuses status={params.row.status} chipSize='small' />;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
    >
      <DataGrid<IncomingBankPayment>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection={false}
        disableSelectionOnClick
        disablePointer={isStatusMatched}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onRowClick={!isStatusMatched ? handleRowClick : undefined}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
      {selectionModel && selectionModel.length > 0 && searchFilter.ownerId && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
          </Box>
        </FixedFooter>
      )}

      {selectedIncomingBankPayment && (
        <IncomingBankPaymentEditModal
          modalOpen={showDetailsModal}
          incomingBankPaymentId={selectedIncomingBankPayment.transaction_id}
          onClose={() => {
            setShowDetailsModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default IncomingBankPaymentDataGrid;
